import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/feature/BackToTop";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import ArticleCard from "@/components/feature/ArticleCard";
import { articles, categories } from "@/mocks/articles";
import { getCategoryName, getCategoryBySlug, sortByDate, sortByHeat } from "@/utils/articles";
import { useSeo, SITE_ORIGIN } from "@/hooks/useSeo";

type SortKey = "latest" | "hot";

export default function Materials() {
  const [searchParams, setSearchParams] = useSearchParams();
  const catSlug = searchParams.get("cat") ?? "";
  const query = searchParams.get("q") ?? "";

  const [sort, setSort] = useState<SortKey>("latest");
  const [keyword, setKeyword] = useState(query);

  useEffect(() => {
    setKeyword(query);
  }, [query]);

  const activeCategory = getCategoryBySlug(catSlug);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    let list = articles.filter((item) => {
      const matchCat = catSlug ? item.categoryId === `cat-${catSlug}` : true;
      if (!matchCat) return false;
      if (!q) return true;
      const haystack = [item.title, item.excerpt, item.tags.join(" "), getCategoryName(item.categoryId)]
        .join(" ")
        .toLowerCase();
      return haystack.includes(q);
    });
    list = sort === "hot" ? sortByHeat(list) : sortByDate(list);
    return list;
  }, [catSlug, query, sort]);

  const pageTitle = activeCategory
    ? `${activeCategory.name}合集｜看料88 今日猛料实时更新`
    : query
      ? `“${query}”相关话题合集｜看料88 热点汇总`
      : "猛料合集｜看料88 最新爆料与热门大瓜实时更新";

  const pageDesc = activeCategory
    ? `${activeCategory.description}看料88 持续更新${activeCategory.name}方向的最新话题与时间线整理。`
    : "看料88 猛料合集页汇总全部话题，支持按栏目筛选和关键词搜索，今日猛料、最新爆料合集与热门大瓜实时更新。";

  useSeo({
    title: pageTitle,
    description: pageDesc,
    keywords: activeCategory
      ? `${activeCategory.name},看料88,今日猛料,热门大瓜,最新爆料合集`
      : "看料88,猛料合集,今日猛料,最新爆料合集,热门大瓜,实时更新料",
    path: "/materials",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        name: activeCategory ? `${activeCategory.name}合集` : "猛料合集",
        url: `${SITE_ORIGIN}/materials`,
        inLanguage: "zh-CN",
        description: pageDesc,
        hasPart: results.slice(0, 12).map((item) => ({
          "@type": "Article",
          headline: item.title,
          url: `${SITE_ORIGIN}/material/${item.slug}`,
          datePublished: item.publishedAt,
        })),
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: SITE_ORIGIN },
          { "@type": "ListItem", position: 2, name: "猛料合集", item: `${SITE_ORIGIN}/materials` },
        ],
      },
    ],
  });

  const updateParams = (next: { cat?: string; q?: string }) => {
    const params = new URLSearchParams(searchParams);
    if (next.cat !== undefined) {
      if (next.cat) params.set("cat", next.cat);
      else params.delete("cat");
    }
    if (next.q !== undefined) {
      if (next.q) params.set("q", next.q);
      else params.delete("q");
    }
    setSearchParams(params, { replace: true });
  };

  const handleSearch = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    updateParams({ q: keyword.trim() });
  };

  return (
    <div className="flex min-h-screen flex-col bg-background-50">
      <SiteHeader />
      <main className="flex-1">
        <div className="border-b border-background-200 bg-background-100">
          <div className="mx-auto w-full max-w-[1400px] px-4 py-8 md:px-8 md:py-10">
            <Breadcrumbs
              items={
                activeCategory
                  ? [{ label: "首页", to: "/" }, { label: "猛料合集", to: "/materials" }, { label: activeCategory.name }]
                  : [{ label: "首页", to: "/" }, { label: "猛料合集" }]
              }
            />
            <h1 className="mt-4 font-heading text-2xl font-black leading-snug tracking-tight text-foreground-950 md:text-4xl">
              {activeCategory ? `${activeCategory.name} · 话题合集` : "猛料合集 · 持续更新中"}
            </h1>
            <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600">
              {activeCategory
                ? activeCategory.description
                : "全部话题按更新时间排列，支持按栏目筛选或直接搜索关键词。每条都附有背景梳理和最新进展。"}
            </p>
          </div>
        </div>

        <div className="mx-auto w-full max-w-[1400px] px-4 py-8 md:px-8 md:py-10">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => updateParams({ cat: "" })}
                className={`cursor-pointer whitespace-nowrap rounded-full px-4 py-2 text-sm transition-colors ${
                  !activeCategory
                    ? "bg-foreground-950 text-background-50"
                    : "border border-background-300 bg-background-50 text-foreground-700 hover:border-foreground-400"
                }`}
              >
                全部
              </button>
              {categories.map((cat) => (
                <button
                  key={cat.id}
                  type="button"
                  onClick={() => updateParams({ cat: cat.slug })}
                  className={`flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full px-4 py-2 text-sm transition-colors ${
                    activeCategory?.id === cat.id
                      ? "bg-foreground-950 text-background-50"
                      : "border border-background-300 bg-background-50 text-foreground-700 hover:border-foreground-400"
                  }`}
                >
                  <i className={`${cat.icon} text-sm`} />
                  {cat.name}
                </button>
              ))}
            </div>

            <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
              <form onSubmit={handleSearch} className="relative" role="search">
                <i className="ri-search-line pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-base text-foreground-400" />
                <input
                  type="search"
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  placeholder="搜索话题关键词"
                  className="h-10 w-full rounded-md border border-background-300 bg-background-50 pl-9 pr-3 text-sm text-foreground-900 outline-none placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-200 sm:w-64"
                />
              </form>
              <div className="flex items-center gap-1 rounded-md bg-background-100 p-1">
                <button
                  type="button"
                  onClick={() => setSort("latest")}
                  className={`flex-1 cursor-pointer whitespace-nowrap rounded-md px-3 py-2 text-sm transition-colors sm:flex-none ${
                    sort === "latest" ? "bg-background-50 text-foreground-950" : "text-foreground-600 hover:text-foreground-900"
                  }`}
                >
                  最新更新
                </button>
                <button
                  type="button"
                  onClick={() => setSort("hot")}
                  className={`flex-1 cursor-pointer whitespace-nowrap rounded-md px-3 py-2 text-sm transition-colors sm:flex-none ${
                    sort === "hot" ? "bg-background-50 text-foreground-950" : "text-foreground-600 hover:text-foreground-900"
                  }`}
                >
                  热度优先
                </button>
              </div>
            </div>
          </div>

          <div className="mt-5 flex flex-wrap items-center gap-3 border-t border-background-200 pt-5 text-xs text-foreground-500">
            <span>
              共找到 <strong className="text-foreground-900">{results.length}</strong> 条话题
            </span>
            {query && (
              <span className="flex items-center gap-1.5 rounded-full bg-secondary-100 px-3 py-1 text-secondary-900">
                关键词：{query}
                <button
                  type="button"
                  onClick={() => updateParams({ q: "" })}
                  className="cursor-pointer text-secondary-700 hover:text-primary-600"
                  aria-label="清除关键词"
                >
                  <i className="ri-close-line" />
                </button>
              </span>
            )}
          </div>

          {results.length > 0 ? (
            <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 lg:gap-6">
              {results.map((item, index) => (
                <ArticleCard
                  key={item.id}
                  article={item}
                  categoryName={getCategoryName(item.categoryId)}
                  rank={sort === "hot" ? index + 1 : undefined}
                />
              ))}
            </div>
          ) : (
            <div className="mt-10 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-100 py-16 text-center">
              <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-200 text-foreground-500">
                <i className="ri-search-eye-line text-2xl" />
              </span>
              <h2 className="mt-4 font-heading text-lg font-semibold text-foreground-900">暂时没有匹配的话题</h2>
              <p className="mt-2 max-w-sm text-sm text-foreground-600">
                换个关键词试试，或者直接浏览全部合集。热门话题通常会在几小时内收录进来。
              </p>
              <Link
                to="/materials"
                className="mt-5 inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                查看全部合集
              </Link>
            </div>
          )}
        </div>
      </main>
      <SiteFooter />
      <BackToTop />
    </div>
  );
}